import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import postsAPI from '../../api/posts.api';

const AdminPostsPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await postsAPI.getPosts(1, 100);
      setPosts(data.posts);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        setDeleting(postId);
        await postsAPI.deletePost(postId);
        setPosts(posts.filter(p => p.id !== postId));
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete post');
      } finally {
        setDeleting(null);
      }
    }
  };

  const handleTogglePublish = async (postId, currentStatus) => {
    try {
      await postsAPI.togglePublish(postId);
      setPosts(posts.map(p =>
        p.id === postId ? { ...p, published: !currentStatus } : p
      ));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update post status');
    }
  };

  return (
    <AdminLayout>
      <div>
        {/* Page Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem'
        }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: 'var(--navy)',
            margin: 0
          }}>
            Manage Posts
          </h1>
          <button
            onClick={() => navigate('/admin/posts/new')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'var(--teal)',
              color: 'white',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '1rem'
            }}
          >
            <Plus size={18} />
            New Post
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#fee8e8',
            border: '1px solid #d96f7b',
            borderRadius: '0.375rem',
            padding: '1rem',
            marginBottom: '2rem',
            color: '#c42a45'
          }}>
            {error}
          </div>
        )}

        {/* Posts Table */}
        <div style={{
          background: 'white',
          borderRadius: '0.5rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading posts...
            </div>
          ) : posts.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No posts yet.{' '}
              <button
                onClick={() => navigate('/admin/posts/new')}
                style={{
                  color: 'var(--teal)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: '600',
                  textDecoration: 'underline'
                }}
              >
                Create your first post
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{ background: 'var(--off-white)', borderBottom: '2px solid var(--border-color)' }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      color: 'var(--text-secondary)',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>Title</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      color: 'var(--text-secondary)',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>Date</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      color: 'var(--text-secondary)',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>Status</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'right',
                      color: 'var(--text-secondary)',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr
                      key={post.id}
                      style={{
                        borderBottom: '1px solid var(--border-color)',
                        transition: 'background 0.2s ease',
                        ':hover': { background: 'var(--off-white)' }
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--off-white)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{
                        padding: '1rem',
                        fontWeight: '500',
                        color: 'var(--navy)'
                      }}>
                        {post.title}
                      </td>
                      <td style={{
                        padding: '1rem',
                        color: 'var(--text-secondary)',
                        fontSize: '0.95rem'
                      }}>
                        {new Date(post.publishedAt || post.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{
                        padding: '1rem'
                      }}>
                        <div style={{
                          display: 'inline-block',
                          padding: '0.35rem 0.75rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          background: post.published ? '#c1f0b6' : '#fff3cd',
                          color: post.published ? '#4a7c3f' : '#856404'
                        }}>
                          {post.published ? 'Published' : 'Draft'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1rem',
                        textAlign: 'right'
                      }}>
                        <div style={{
                          display: 'flex',
                          gap: '0.5rem',
                          justifyContent: 'flex-end'
                        }}>
                          <button
                            onClick={() => handleTogglePublish(post.id, post.published)}
                            title={post.published ? 'Unpublish' : 'Publish'}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justify: 'center',
                              padding: '0.5rem 0.75rem',
                              background: 'none',
                              border: '1px solid #ccc',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              color: 'var(--text-secondary)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = 'var(--teal)';
                              e.currentTarget.style.color = 'var(--teal)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'var(--border-color)';
                              e.currentTarget.style.color = 'var(--text-secondary)';
                            }}
                          >
                            {post.published ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button
                            onClick={() => navigate(`/admin/posts/${post.id}/edit`)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justify: 'center',
                              padding: '0.5rem 0.75rem',
                              background: 'none',
                              border: '1px solid #ccc',
                              borderRadius: '0.375rem',
                              cursor: 'pointer',
                              color: 'var(--text-secondary)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = 'var(--teal)';
                              e.currentTarget.style.color = 'var(--teal)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'var(--border-color)';
                              e.currentTarget.style.color = 'var(--text-secondary)';
                            }}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            disabled={deleting === post.id}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justify: 'center',
                              padding: '0.5rem 0.75rem',
                              background: 'none',
                              border: '1px solid #ccc',
                              borderRadius: '0.375rem',
                              cursor: deleting === post.id ? 'not-allowed' : 'pointer',
                              color: 'var(--text-secondary)',
                              transition: 'all 0.2s ease',
                              opacity: deleting === post.id ? '0.5' : '1'
                            }}
                            onMouseEnter={(e) => {
                              if (deleting !== post.id) {
                                e.currentTarget.style.borderColor = '#d96f7b';
                                e.currentTarget.style.color = '#d96f7b';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'var(--border-color)';
                              e.currentTarget.style.color = 'var(--text-secondary)';
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPostsPage;
